import { useState, useEffect, useCallback, useRef } from 'react'
import { usePubSub, ErrorTrackingAPI, employeeServices } from '@services'
import { storageHelpers } from '@utils'
import { parseGroupId, customToast, CONNECTION_STATUS } from '@common'
import { usePubsubRedux } from './useRedux'

// Module-level flag to prevent duplicate toasts across all hook instances
// This ensures only one toast is shown even with React StrictMode double execution
let globalReconnectToastShown = false
let globalReconnectToastResetTime = 0

export const useCashRegister = () => {
    const [isAutoJoining, setIsAutoJoining] = useState(false)
    const isAutoJoiningRef = useRef(false)
    const { setActiveGroupId } = usePubsubRedux()
    const { groups, joinGroup, waitUntilConnected, connectionStatus } = usePubSub()
    const previousConnectionStatus = useRef(connectionStatus)

    const joinedGroupName = Array.isArray(groups) && groups.length > 0 ? groups[0] : null

    // Function to join a specific group
    const joinGroupByName = useCallback(
        async groupName => {
            // Use ref to prevent concurrent joins without triggering re-renders
            if (!groupName || isAutoJoiningRef.current || joinedGroupName === groupName) return

            isAutoJoiningRef.current = true
            setIsAutoJoining(true)

            try {
                // waitUntilConnected already ensures connection is established
                // No need to check isConnected as it may have delayed state update
                await waitUntilConnected(5000)

                await joinGroup(groupName)
            } catch (err) {
                ErrorTrackingAPI.sendError(err, 'useCashRegister', 'joinGroupByName')
                throw err
            } finally {
                isAutoJoiningRef.current = false
                setIsAutoJoining(false)
            }
        },
        [joinGroup, waitUntilConnected, joinedGroupName]
    )

    // Auto-join group when page loads - wait for connection first
    // This is a fallback mechanism that only runs if no employee token exists
    // (CheckoutAssistantPage will handle join when employee token exists)
    // This avoids race condition with CheckoutAssistantPage's fetchEmployeeAndConnect
    useEffect(() => {
        const autoJoinGroup = async () => {
            // Use ref to guard against concurrent executions without causing re-renders
            if (isAutoJoiningRef.current || joinedGroupName) return

            // Check if employee token exists - if so, let CheckoutAssistantPage handle the join
            const tokens = employeeServices.getEmployeeTokens()
            if (tokens.accessToken && tokens.expiredAt && new Date(tokens.expiredAt) > new Date()) {
                // Employee token exists, CheckoutAssistantPage will handle the join
                // Skip this fallback mechanism
                return
            }

            isAutoJoiningRef.current = true
            setIsAutoJoining(true)

            try {
                // waitUntilConnected already ensures connection is established
                // No need to check isConnected as it may have delayed state update
                await waitUntilConnected(5000)

                // Get the last used cash register group name from localStorage
                const cashRegisterGroup = storageHelpers.getCheckoutAssistantCashRegisterId()
                if (cashRegisterGroup) {
                    await joinGroup(cashRegisterGroup)
                }
            } catch (err) {
                ErrorTrackingAPI.sendError(err, 'useCashRegister', 'autoJoinGroup')
            } finally {
                isAutoJoiningRef.current = false
                setIsAutoJoining(false)
            }
        }

        autoJoinGroup()
    }, [joinGroup, waitUntilConnected, joinedGroupName])

    // Save the current group to localStorage when it changes
    useEffect(() => {
        if (joinedGroupName) {
            storageHelpers.setCheckoutAssistantCashRegisterId(joinedGroupName)
            // Extract groupId using unified parsing function
            // We use formattedId (e.g., 'CASH_REGISTER-123') or numericId (e.g., '123') as fallback
            const { formattedId, numericId } = parseGroupId(joinedGroupName)
            setActiveGroupId(formattedId || numericId)
        }
    }, [joinedGroupName, setActiveGroupId])

    // Monitor connection status changes to show toast on reconnection
    // Only depend on connectionStatus to avoid duplicate toasts when joinedGroupName changes
    useEffect(() => {
        const prevStatus = previousConnectionStatus.current
        const currentStatus = connectionStatus

        // Reset global toast flag when disconnected (with debounce to allow for rapid reconnects)
        if (currentStatus === CONNECTION_STATUS.DISCONNECTED) {
            const now = Date.now()
            // Reset flag after 1 second of being disconnected to allow new reconnect toasts
            if (now - globalReconnectToastResetTime > 1000) {
                globalReconnectToastShown = false
                globalReconnectToastResetTime = now
            }
        }

        // Show toast when starting to reconnect (status changes from disconnected to connecting)
        // and there was a previous group that needs to be rejoined
        // Use module-level flag to prevent duplicates from React StrictMode double execution
        if (prevStatus === CONNECTION_STATUS.DISCONNECTED && currentStatus === CONNECTION_STATUS.CONNECTING) {
            // Check if there's a stored group that will be rejoined
            const storedGroup = storageHelpers.getCheckoutAssistantCashRegisterId()
            if (storedGroup && !globalReconnectToastShown) {
                // Set global flag immediately to prevent duplicate execution
                // This works even with React StrictMode because it's module-level, not component-level
                globalReconnectToastShown = true
                customToast.info('Reconnecting to Checkout Assistant...', { duration: 3000 })
            }
        }

        previousConnectionStatus.current = currentStatus
    }, [connectionStatus])

    return {
        joinedGroupName,
        isAutoJoining,
        joinGroupByName,
    }
}
